import { createRoutine } from 'redux-saga-routines'
import { put, call, cancel, take, takeEvery, takeLatest, fork, select, delay } from '@redux-saga/core/effects'
import * as taskService from 'services/taskService'
import { toast } from 'react-toastify'
import { getApiErrors } from 'utils/errors'
import { invalidateFormTemplatesCache } from 'utils/swCacheControl'
import { PRIVATE_PATHS, redirect } from 'utils/paths'
import { selectTaskFilters } from './selectors'
import db, { cacheTasksMerged } from 'services/offlineDb'
import { applyTaskFilters, enqueueSyncAction, generateTempId, deduplicateSyncEntry, cleanupOrphanedFiles } from 'services/offlineSync'
import { updateSyncCountsRoutine } from 'ducks/offline/actions'
import { scheduleTaskPrefetch } from 'services/offlinePrefetch'


// Use Redux store for online check — navigator.onLine lies on mobile
const getIsOnline = () => {
  const store = window.reduxStore
  return store ? (store.getState().offline?.isOnline ?? navigator.onLine) : navigator.onLine
}

const isNetworkError = (error) =>
  error?.message === 'Network Error' || error?.code === 'ERR_NETWORK' || !getIsOnline()

const showErrorIfOnline = (error) => {
  if (!isNetworkError(error)) {
    const msg = getApiErrors(error)
    msg?.trim() && toast.error(msg)
  }
}

// Tasks
export const getTasksRoutine = createRoutine('GET_TASKS')
export const getTaskRoutine = createRoutine('GET_TASK')
export const createTaskRoutine = createRoutine('CREATE_TASK')
export const updateTaskRoutine = createRoutine('UPDATE_TASK')
export const deleteTaskRoutine = createRoutine('DELETE_TASK')
export const assignTaskRoutine = createRoutine('ASSIGN_TASK')
export const submitTaskRoutine = createRoutine('SUBMIT_TASK')
export const approveTaskRoutine = createRoutine('APPROVE_TASK')
export const rejectTaskRoutine = createRoutine('REJECT_TASK')
export const starTaskRoutine = createRoutine('STAR_TASK')
export const syncBlockersRoutine = createRoutine('SYNC_BLOCKERS')
export const updateApproversRoutine = createRoutine('UPDATE_APPROVERS')
export const assignExternalUserRoutine = createRoutine('ASSIGN_EXTERNAL_USER')
export const removeExternalAssigneeRoutine = createRoutine('REMOVE_EXTERNAL_ASSIGNEE')
export const getTaskStatsRoutine = createRoutine('GET_TASK_STATS')
export const refreshTaskCountsRoutine = createRoutine('REFRESH_TASK_COUNTS')
export const replicateTaskRoutine = createRoutine('REPLICATE_TASK')
export const holdTaskRoutine = createRoutine('HOLD_TASK')
export const resumeTaskRoutine = createRoutine('RESUME_TASK')
export const signOffTaskRoutine = createRoutine('SIGN_OFF_TASK')
export const bulkSignOffRoutine = createRoutine('BULK_SIGN_OFF')

// Task Settings
export const getTaskSettingsRoutine = createRoutine('GET_TASK_SETTINGS')
export const updateTaskSettingsRoutine = createRoutine('UPDATE_TASK_SETTINGS')

// Project Dashboard
export const getProjectDashboardRoutine = createRoutine('GET_PROJECT_DASHBOARD')

// Projects
export const getProjectsRoutine = createRoutine('GET_PROJECTS')
export const createProjectRoutine = createRoutine('CREATE_PROJECT')
export const updateProjectRoutine = createRoutine('UPDATE_PROJECT')
export const deleteProjectRoutine = createRoutine('DELETE_PROJECT')

// Teams
export const getTeamsRoutine = createRoutine('GET_TEAMS')
export const createTeamRoutine = createRoutine('CREATE_TEAM')
export const updateTeamRoutine = createRoutine('UPDATE_TEAM')
export const deleteTeamRoutine = createRoutine('DELETE_TEAM')
export const addTeamMemberRoutine = createRoutine('ADD_TEAM_MEMBER')
export const removeTeamMemberRoutine = createRoutine('REMOVE_TEAM_MEMBER')

// Form Templates
export const getFormTemplatesRoutine = createRoutine('GET_FORM_TEMPLATES')
export const getFormTemplateRoutine = createRoutine('GET_FORM_TEMPLATE')
export const createFormTemplateRoutine = createRoutine('CREATE_FORM_TEMPLATE')
export const updateFormTemplateRoutine = createRoutine('UPDATE_FORM_TEMPLATE')
export const deleteFormTemplateRoutine = createRoutine('DELETE_FORM_TEMPLATE')
export const publishFormTemplateRoutine = createRoutine('PUBLISH_FORM_TEMPLATE')
export const cloneFormTemplateRoutine = createRoutine('CLONE_FORM_TEMPLATE')

// Template Assignments (renewal)
export const getTemplateAssignmentsRoutine = createRoutine('GET_TEMPLATE_ASSIGNMENTS')
export const createTemplateAssignmentRoutine = createRoutine('CREATE_TEMPLATE_ASSIGNMENT')
export const deleteTemplateAssignmentRoutine = createRoutine('DELETE_TEMPLATE_ASSIGNMENT')

export const saveAndSubmitTaskRoutine = createRoutine('SAVE_AND_SUBMIT_TASK')

// Form Responses
export const saveFormResponseRoutine = createRoutine('SAVE_FORM_RESPONSE')
export const saveApproverFormResponseRoutine = createRoutine('SAVE_APPROVER_FORM_RESPONSE')
export const getFormVersionsRoutine = createRoutine('GET_FORM_VERSIONS')
export const getConflictsRoutine = createRoutine('GET_CONFLICTS')

// Bulk Operations
export const bulkDeleteTasksRoutine = createRoutine('BULK_DELETE_TASKS')
export const bulkUpdateTasksRoutine = createRoutine('BULK_UPDATE_TASKS')
export const bulkAssignTasksRoutine = createRoutine('BULK_ASSIGN_TASKS')

// Selection
export const selectTaskRoutine = createRoutine('SELECT_TASK')
export const selectAllTasksRoutine = createRoutine('SELECT_ALL_TASKS')
export const clearSelectionRoutine = createRoutine('CLEAR_SELECTION')

// Filters
export const setTaskFiltersRoutine = createRoutine('SET_TASK_FILTERS')
export const clearTaskFiltersRoutine = createRoutine('CLEAR_TASK_FILTERS')

// --- Saga functions ---

const STATUS_CATEGORIES = ['awaiting_approval', 'on_hold', 'completed']

export const categoryFilterParams = category => {
  if (!category || category === 'all_tasks') return {}
  if (STATUS_CATEGORIES.includes(category)) return { 'filter[status]': category }
  if (category.startsWith('project_')) return { 'filter[task_project_id]': category.replace('project_', '') }
  switch (category) {
    case 'my_tasks': return { 'filter[assigned_to]': 'me' }
    case 'starred': return { 'filter[starred]': 1 }
    case 'today': return { 'filter[due_today]': 1 }
    case 'due_this_week': return { 'filter[due_this_week]': 1 }
    case 'overdue': return { 'filter[overdue]': 1 }
    case 'my_overdue': return { 'filter[overdue]': 1, 'filter[assigned_to]': 'me' }
    case 'completed_late': return { 'filter[completed_late]': 1 }
    default: return {}
  }
}

export function buildTaskParams(filters, page, stable) {
  const params = { page: page || 1, limit: 50 }
  params.sort = filters.sort || '-freshness'
  if (filters.status && filters.status !== 'all') {
    if (filters.status === 'overdue') {
      params['filter[overdue]'] = 1
    } else {
      params['filter[status]'] = filters.status
    }
  }
  if (filters.open) {
    params['filter[open]'] = 1
  }
  if (filters.completedLate) {
    params['filter[completed_late]'] = 1
  }
  if (filters.assignedTo) {
    params['filter[assigned_to]'] = filters.assignedTo
  }
  if (filters.approverId) {
    params['filter[approver]'] = filters.approverId
  }
  if (filters.buildingId) {
    params['filter[building_passport_id]'] = filters.buildingId
  }
  if (filters.portfolioId) {
    params['filter[portfolio_id]'] = filters.portfolioId
  }
  if (filters.estateId) {
    params['filter[estate_id]'] = filters.estateId
  }
  if (filters.taskDomainId) {
    params['filter[task_domain_id]'] = filters.taskDomainId
  }
  if (filters.projectId) {
    params['filter[task_project_id]'] = filters.projectId
  }
  if (filters.search) {
    params['filter[search]'] = filters.search
  }
  if (filters.starred) {
    params['filter[starred]'] = 1
  }
  if (filters.priority) {
    params['filter[priority]'] = filters.priority
  }
  if (filters.formTemplateId) {
    params['filter[form_template_id]'] = filters.formTemplateId
  }
  if (filters.deadlineFrom) {
    params['filter[deadline][from]'] = filters.deadlineFrom
  }
  if (filters.teamId) {
    params['filter[team_id]'] = filters.teamId
  }
  if (filters.type) {
    params['filter[type]'] = filters.type
  }
  if (filters.origin) {
    params['filter[origin]'] = filters.origin
  }
  if (filters.deadlineTo) {
    params['filter[deadline][to]'] = filters.deadlineTo
  }
  if (filters.createdFrom) {
    params['filter[created][from]'] = filters.createdFrom
  }
  if (filters.createdTo) {
    params['filter[created][to]'] = filters.createdTo
  }
  Object.assign(params, categoryFilterParams(filters.category))
  params.window = filters.window || 'week'
  return params
}

export const taskQueryKey = filters => {
  const { page, limit, ...rest } = buildTaskParams(filters || {})
  return JSON.stringify(Object.keys(rest).sort().map(key => [key, String(rest[key])]))
}

// Tasks
export function* getTasks({ payload }) {
  yield put(getTasksRoutine.request())
  try {
    if (payload?.fromCache && getIsOnline()) {
      const cached = yield call(() => db.tasks.toArray())
      if (cached.length > 0) {
        const filters = yield select(selectTaskFilters)
        const filtered = applyTaskFilters(cached, filters)
        yield put(getTasksRoutine.success({
          data: filtered,
          totalPages: 1,
          currentPage: 1,
          total: filtered.length,
          replace: true,
          queryKey: taskQueryKey(filters)
        }))
        yield call(scheduleTaskPrefetch, { window: filters.window || 'week' })
        return
      }
    }
    if (getIsOnline()) {
      const filters = yield select(selectTaskFilters)
      const params = buildTaskParams(filters, payload?.page, payload?.stable)
      const result = yield call(taskService.getTasks, params)
      const { data, meta, windows, viewCounts } = result.data
      // Cache to IDB
      if (data?.length > 0) {
        yield call(() => cacheTasksMerged(data))
      }
      yield put(getTasksRoutine.success({
        data,
        totalPages: meta?.lastPage || 1,
        currentPage: meta?.currentPage || 1,
        total: meta?.total ?? (data?.length || 0),
        windows,
        viewCounts,
        queryKey: taskQueryKey(filters)
      }))
      yield call(scheduleTaskPrefetch, { window: filters.window || 'week' })
    } else {
      // OFFLINE: read from IDB + filter client-side
      const allTasks = yield call(() => db.tasks.toArray())
      const filters = yield select(selectTaskFilters)
      const currentUserId = yield select(state => state.auth?.user?.id)
      const filtered = applyTaskFilters(allTasks, { ...filters, currentUserId })
      yield put(getTasksRoutine.success({
        data: filtered,
        totalPages: 1,
        currentPage: 1,
        total: filtered.length,
        queryKey: taskQueryKey(filters)
      }))
    }
  } catch (error) {
    if (error?.message === 'Network Error' || error?.code === 'ERR_NETWORK') {
      // Network failed — try IDB fallback
      try {
        const allTasks = yield call(() => db.tasks.toArray())
        const filters = yield select(selectTaskFilters)
        const currentUserId = yield select(state => state.auth?.user?.id)
        const filtered = applyTaskFilters(allTasks, { ...filters, currentUserId })
        yield put(getTasksRoutine.success({ data: filtered, totalPages: 1, currentPage: 1, total: filtered.length, queryKey: taskQueryKey(filters) }))
        return
      } catch (e) { /* fall through */ }
    }
    showErrorIfOnline(error)
    yield put(getTasksRoutine.failure(error))
  }
}

function* getTaskStats() {
  yield put(getTaskStatsRoutine.request())
  try {
    if (getIsOnline()) {
      const result = yield call(taskService.getTaskStats)
      yield put(getTaskStatsRoutine.success(result.data.data))
    } else {
      const tasks = yield call(() => db.tasks.toArray())
      yield put(getTaskStatsRoutine.success({
        total: tasks.length,
        draft: tasks.filter(t => t.status === 'draft').length,
        open: tasks.filter(t => t.status === 'open').length,
        inProgress: tasks.filter(t => t.status === 'in_progress').length,
        submitted: tasks.filter(t => t.status === 'submitted').length,
        completed: tasks.filter(t => t.status === 'completed').length,
        overdue: tasks.filter(t => t.status !== 'completed' && t.deadline && new Date(t.deadline) < new Date()).length,
        _offline: true
      }))
    }
  } catch (error) {
    if (error?.message === 'Network Error' || error?.code === 'ERR_NETWORK') {
      try {
        const tasks = yield call(() => db.tasks.toArray())
        yield put(getTaskStatsRoutine.success({
          total: tasks.length,
          draft: tasks.filter(t => t.status === 'draft').length,
          open: tasks.filter(t => t.status === 'open').length,
          inProgress: tasks.filter(t => t.status === 'in_progress').length,
          submitted: tasks.filter(t => t.status === 'submitted').length,
          completed: tasks.filter(t => t.status === 'completed').length,
          overdue: tasks.filter(t => t.status !== 'completed' && t.deadline && new Date(t.deadline) < new Date()).length,
          _offline: true
        }))
        return
      } catch (e) { /* fall through */ }
    }
    yield put(getTaskStatsRoutine.failure(error))
  }
}

function* getTask({ payload }) {
  yield put(getTaskRoutine.request())
  try {
    if (getIsOnline()) {
      const result = yield call(taskService.getTask, payload)
      yield call(() => db.tasks.put(result.data.data))
      yield put(getTaskRoutine.success(result.data.data))
    } else {
      const task = yield call(() => db.tasks.get(payload))
      if (task) {
        yield put(getTaskRoutine.success(task))
      } else {
        toast.error('Task not available offline')
        yield put(getTaskRoutine.failure(new Error('Task not cached')))
      }
    }
  } catch (error) {
    if (error?.message === 'Network Error' || error?.code === 'ERR_NETWORK') {
      const task = yield call(() => db.tasks.get(payload))
      if (task) { yield put(getTaskRoutine.success(task)); return }
    }
    if (error?.response?.status === 404) {
      yield call(() => db.tasks.delete(payload))
      toast.info('This task is no longer available. It may have been deleted.')
      yield put(getTaskRoutine.failure({ notFound: true }))
      return
    }
    showErrorIfOnline(error)
    yield put(getTaskRoutine.failure(error))
  }
}

export function* createTask({ payload: rawPayload }) {
  const { onSuccess, ...payload } = rawPayload || {}
  const notifyCreated = created => { if (typeof onSuccess === 'function') onSuccess(created) }
  yield put(createTaskRoutine.request())
  try {
    if (getIsOnline()) {
      const result = yield call(taskService.createTask, payload)
      const createdTasks = result.data.data
      if (Array.isArray(createdTasks)) {
        yield call(() => db.tasks.bulkPut(createdTasks))
      } else {
        yield call(() => db.tasks.put(createdTasks))
      }
      yield put(createTaskRoutine.success(createdTasks))
      yield call(notifyCreated, createdTasks)
      yield put(getProjectsRoutine())
      toast.success(
        Array.isArray(createdTasks)
          ? `${createdTasks.length} tasks created successfully`
          : 'Task created successfully'
      )
    } else {
      const tempId = generateTempId()
      const tempTask = { ...payload, id: tempId, status: 'draft', _offline: true, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
      yield call(() => db.tasks.put(tempTask))
      yield call(enqueueSyncAction, { type: 'CREATE_TASK', payload, tempId })
      yield put(createTaskRoutine.success(tempTask))
      yield call(notifyCreated, tempTask)
      yield put(updateSyncCountsRoutine())
      toast.info('Task saved offline. Will sync when connected.')
    }
  } catch (error) {
    if (isNetworkError(error)) {
      try {
        const tempId = generateTempId()
        const tempTask = { ...payload, id: tempId, status: 'draft', _offline: true, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
        yield call(() => db.tasks.put(tempTask))
        yield call(enqueueSyncAction, { type: 'CREATE_TASK', payload, tempId })
        yield put(createTaskRoutine.success(tempTask))
        yield call(notifyCreated, tempTask)
        yield put(updateSyncCountsRoutine())
        toast.info('Task saved offline. Will sync when connected.')
        return
      } catch (e) { /* fall through */ }
    }
    showErrorIfOnline(error)
    yield put(createTaskRoutine.failure(error))
  }
}

function* replicateTask({ payload }) {
  yield put(replicateTaskRoutine.request())
  try {
    const result = yield call(taskService.replicateTask, payload)
    const newTask = result.data.data
    yield put(replicateTaskRoutine.success(newTask))
    yield put(getProjectsRoutine())
    toast.success('Task replicated as new draft')
    redirect(PRIVATE_PATHS.taskDetails.replace(':id', newTask.id) + '?editTitle=true')
  } catch (error) {
    showErrorIfOnline(error)
    yield put(replicateTaskRoutine.failure(error))
  }
}

function* updateTask({ payload }) {
  yield put(updateTaskRoutine.request())
  try {
    if (getIsOnline()) {
      const result = yield call(taskService.updateTask, payload)
      yield call(() => db.tasks.put(result.data.data))
      yield put(updateTaskRoutine.success(result.data.data))
      yield put(getProjectsRoutine())
      toast.success('Task updated successfully')
    } else {
      const existing = yield call(() => db.tasks.get(payload.id))
      const updated = { ...existing, ...payload, updatedAt: new Date().toISOString(), _offline: true }
      yield call(() => db.tasks.put(updated))
      yield call(enqueueSyncAction, { type: 'UPDATE_TASK', payload })
      yield put(updateTaskRoutine.success(updated))
      yield put(updateSyncCountsRoutine())
      toast.info('Changes saved offline.')
    }
  } catch (error) {
    if (isNetworkError(error)) {
      try {
        const existing = yield call(() => db.tasks.get(payload.id))
        const updated = { ...existing, ...payload, updatedAt: new Date().toISOString(), _offline: true }
        yield call(() => db.tasks.put(updated))
        yield call(enqueueSyncAction, { type: 'UPDATE_TASK', payload })
        yield put(updateTaskRoutine.success(updated))
        yield put(updateSyncCountsRoutine())
        toast.info('Changes saved offline.')
        return
      } catch (e) { /* fall through */ }
    }
    showErrorIfOnline(error)
    yield put(updateTaskRoutine.failure(error))
  }
}

function* deleteTask({ payload }) {
  yield put(deleteTaskRoutine.request())
  try {
    if (getIsOnline()) {
      yield call(taskService.deleteTask, payload)
      yield call(() => db.tasks.delete(payload))
      yield put(deleteTaskRoutine.success(payload))
      yield put(getProjectsRoutine())
      toast.success('Task deleted successfully')
    } else {
      yield call(() => db.tasks.delete(payload))
      yield call(cleanupOrphanedFiles, payload)
      yield call(enqueueSyncAction, { type: 'DELETE_TASK', payload })
      yield put(deleteTaskRoutine.success(payload))
      yield put(updateSyncCountsRoutine())
      toast.info('Task will be deleted when back online.')
    }
  } catch (error) {
    if (isNetworkError(error)) {
      try {
        yield call(() => db.tasks.delete(payload))
        yield call(cleanupOrphanedFiles, payload)
        yield call(enqueueSyncAction, { type: 'DELETE_TASK', payload })
        yield put(deleteTaskRoutine.success(payload))
        yield put(updateSyncCountsRoutine())
        toast.info('Task will be deleted when back online.')
        return
      } catch (e) { /* fall through */ }
    }
    const status = error?.response?.status
    if (status === 403) {
      toast.error('You do not have permission to delete this task')
    } else {
      showErrorIfOnline(error)
    }
    yield put(deleteTaskRoutine.failure(error))
  }
}

function* assignTask({ payload }) {
  yield put(assignTaskRoutine.request())
  if (!getIsOnline()) {
    toast.warning('Task assignment is not available offline. Please connect to the internet.')
    yield put(assignTaskRoutine.failure(new Error('Offline')))
    return
  }
  try {
    const result = yield call(taskService.assignTask, payload)
    yield put(assignTaskRoutine.success(result.data.data))
    yield put(updateTaskRoutine.success(result.data.data))
    toast.success('Task assigned successfully')
  } catch (error) {
    const status = error?.response?.status
    if (status === 403) {
      toast.error('You do not have permission to assign this task')
    } else if (isNetworkError(error)) {
      toast.warning('Task assignment is not available offline. Please connect to the internet.')
    } else {
      const msg = getApiErrors(error)
      msg?.trim() && toast.error(msg)
    }
    yield put(assignTaskRoutine.failure(error))
  }
}

function* updateApprovers({ payload }) {
  yield put(updateApproversRoutine.request())
  if (!getIsOnline()) {
    toast.warning('Updating approvers is not available offline. Please connect to the internet.')
    yield put(updateApproversRoutine.failure(new Error('Offline')))
    return
  }
  try {
    const result = yield call(taskService.updateApprovers, payload)
    yield put(updateApproversRoutine.success(result.data.data))
    yield put(updateTaskRoutine.success(result.data.data))
    toast.success('Approvers updated successfully')
  } catch (error) {
    if (isNetworkError(error)) {
      toast.warning('Updating approvers is not available offline. Please connect to the internet.')
    } else {
      showErrorIfOnline(error)
    }
    yield put(updateApproversRoutine.failure(error))
  }
}

function* submitTask({ payload }) {
  yield put(submitTaskRoutine.request())
  try {
    if (getIsOnline()) {
      const result = yield call(taskService.submitTask, payload)
      yield call(() => db.tasks.put(result.data.data))
      yield put(submitTaskRoutine.success(result.data.data))
      yield put(updateTaskRoutine.success(result.data.data))
      yield put(getProjectsRoutine())
      const newStatus = result.data.data?.status
      toast.success(newStatus === 'completed' ? 'Task submitted and completed' : 'Task submitted for approval')
    } else {
      const existing = yield call(() => db.tasks.get(payload))
      const updated = { ...existing, status: 'submitted', _offline: true, updatedAt: new Date().toISOString() }
      yield call(() => db.tasks.put(updated))
      yield call(enqueueSyncAction, { type: 'SUBMIT_TASK', payload })
      yield put(submitTaskRoutine.success(updated))
      yield put(updateTaskRoutine.success(updated))
      yield put(updateSyncCountsRoutine())
      toast.info('Task submitted offline. Will sync when connected.')
    }
  } catch (error) {
    if (isNetworkError(error)) {
      try {
        const existing = yield call(() => db.tasks.get(payload))
        const updated = { ...existing, status: 'submitted', _offline: true, updatedAt: new Date().toISOString() }
        yield call(() => db.tasks.put(updated))
        yield call(enqueueSyncAction, { type: 'SUBMIT_TASK', payload })
        yield put(submitTaskRoutine.success(updated))
        yield put(updateTaskRoutine.success(updated))
        yield put(updateSyncCountsRoutine())
        toast.info('Task submitted offline. Will sync when connected.')
        return
      } catch (e) { /* fall through */ }
    }
    const status = error?.response?.status
    const message = error?.response?.data?.message || ''
    if (status === 403) {
      toast.error('You do not have permission to submit this task')
    } else if (message.toLowerCase().includes('requires evidence')) {
      toast.error('Attach a file or photo before submitting. This task requires evidence.')
    } else {
      showErrorIfOnline(error)
    }
    yield put(submitTaskRoutine.failure(error))
  }
}

function* saveAndSubmitTask({ payload }) {
  const { taskId, responses, currentVersion, submissionLocation } = payload
  yield put(saveAndSubmitTaskRoutine.request())
  try {
    if (!getIsOnline()) {
      // OFFLINE: save form + enqueue submit
      const existing = yield call(() => db.tasks.get(taskId))
      const updated = {
        ...existing,
        formResponse: { ...existing?.formResponse, responses, currentVersion: existing?.formResponse?.currentVersion || currentVersion || 0 },
        status: 'submitted',
        _offline: true,
        updatedAt: new Date().toISOString()
      }
      yield call(() => db.tasks.put(updated))
      yield call(deduplicateSyncEntry, 'SAVE_FORM_RESPONSE', taskId)
      yield call(enqueueSyncAction, { type: 'SAVE_FORM_RESPONSE', payload: { taskId, responses, currentVersion, submissionLocation } })
      yield call(enqueueSyncAction, { type: 'SUBMIT_TASK', payload: taskId })
      yield put(saveFormResponseRoutine.success(updated))
      yield put(submitTaskRoutine.success(updated))
      yield put(updateTaskRoutine.success(updated))
      yield put(updateSyncCountsRoutine())
      yield put(saveAndSubmitTaskRoutine.success(updated))
      toast.info('Task submitted offline. Will sync when connected.')
      return
    }
    // Step 1: Save form response (+location)
    const saveResult = yield call(taskService.saveFormResponse, { taskId, responses, currentVersion, submissionLocation })
    yield put(saveFormResponseRoutine.success(saveResult.data.data))
    yield put(updateTaskRoutine.success(saveResult.data.data))
    // Step 2: Submit task
    const submitResult = yield call(taskService.submitTask, taskId)
    yield put(submitTaskRoutine.success(submitResult.data.data))
    yield put(updateTaskRoutine.success(submitResult.data.data))
    yield put(getProjectsRoutine())
    const newStatus = submitResult.data.data?.status
    toast.success(newStatus === 'completed' ? 'Task submitted and completed' : 'Task submitted for approval')
    yield put(saveAndSubmitTaskRoutine.success(submitResult.data.data))
  } catch (error) {
    // Network error fallback — save offline
    if (isNetworkError(error)) {
      try {
        const existing = yield call(() => db.tasks.get(taskId))
        const svrVersion = existing?.formResponse?.currentVersion || currentVersion || 0
        const updated = {
          ...existing,
          formResponse: { ...existing?.formResponse, responses, currentVersion: svrVersion },
          status: 'submitted',
          _offline: true,
          updatedAt: new Date().toISOString()
        }
        yield call(() => db.tasks.put(updated))
        yield call(deduplicateSyncEntry, 'SAVE_FORM_RESPONSE', taskId)
        yield call(enqueueSyncAction, { type: 'SAVE_FORM_RESPONSE', payload: { taskId, responses, currentVersion: svrVersion } })
        yield call(enqueueSyncAction, { type: 'SUBMIT_TASK', payload: taskId })
        yield put(saveFormResponseRoutine.success(updated))
        yield put(submitTaskRoutine.success(updated))
        yield put(updateTaskRoutine.success(updated))
        yield put(updateSyncCountsRoutine())
        yield put(saveAndSubmitTaskRoutine.success(updated))
        toast.info('Task submitted offline. Will sync when connected.')
        return
      } catch (e) { /* fall through */ }
    }
    if (error?.response?.status === 409) {
      // Silent retry once with the server's version before alarming the user
      // — covers the common race between in-flight auto-save and an
      // explicit submit.
      const serverVersion = error?.response?.data?.currentVersion ?? error?.response?.data?.current_version
      if (!payload._retried409 && serverVersion != null) {
        try {
          const retrySave = yield call(taskService.saveFormResponse, { taskId, responses, currentVersion: serverVersion, submissionLocation })
          yield put(saveFormResponseRoutine.success(retrySave.data.data))
          yield put(updateTaskRoutine.success(retrySave.data.data))
          const retrySubmit = yield call(taskService.submitTask, taskId)
          yield put(submitTaskRoutine.success(retrySubmit.data.data))
          yield put(updateTaskRoutine.success(retrySubmit.data.data))
          yield put(getProjectsRoutine())
          const newStatus = retrySubmit.data.data?.status
          toast.success(newStatus === 'completed' ? 'Task submitted and completed' : 'Task submitted for approval')
          yield put(saveAndSubmitTaskRoutine.success(retrySubmit.data.data))
          return
        } catch (retryError) {
          if (retryError?.response?.status === 409) {
            toast.warning('This form was updated by someone else. Refreshing...')
            yield put(getTaskRoutine(taskId))
          } else {
            showErrorIfOnline(retryError)
          }
          yield put(saveAndSubmitTaskRoutine.failure(retryError))
          return
        }
      }
      toast.warning('This form was updated by someone else. Refreshing...')
      yield put(getTaskRoutine(taskId))
    } else if (error?.response?.status === 403) {
      toast.error('You do not have permission to submit this task')
    } else {
      showErrorIfOnline(error)
    }
    yield put(saveAndSubmitTaskRoutine.failure(error))
  }
}

function* approveTask({ payload }) {
  yield put(approveTaskRoutine.request())
  try {
    if (getIsOnline()) {
      const result = yield call(taskService.approveTask, payload)
      yield call(() => db.tasks.put(result.data.data))
      yield put(approveTaskRoutine.success(result.data.data))
      yield put(updateTaskRoutine.success(result.data.data))
      yield put(getProjectsRoutine())
      toast.success('Task approved')
    } else {
      const existing = yield call(() => db.tasks.get(payload))
      const updated = { ...existing, status: 'approved', _offline: true, updatedAt: new Date().toISOString() }
      yield call(() => db.tasks.put(updated))
      yield call(enqueueSyncAction, { type: 'APPROVE_TASK', payload })
      yield put(approveTaskRoutine.success(updated))
      yield put(updateTaskRoutine.success(updated))
      yield put(updateSyncCountsRoutine())
      toast.info('Task approved offline.')
    }
  } catch (error) {
    if (isNetworkError(error)) {
      try {
        const existing = yield call(() => db.tasks.get(payload))
        const updated = { ...existing, status: 'approved', _offline: true, updatedAt: new Date().toISOString() }
        yield call(() => db.tasks.put(updated))
        yield call(enqueueSyncAction, { type: 'APPROVE_TASK', payload })
        yield put(approveTaskRoutine.success(updated))
        yield put(updateTaskRoutine.success(updated))
        yield put(updateSyncCountsRoutine())
        toast.info('Task approved offline.')
        return
      } catch (e) { /* fall through */ }
    }
    const status = error?.response?.status
    if (status === 403) {
      toast.error('You do not have permission to approve this task')
    } else {
      showErrorIfOnline(error)
    }
    yield put(approveTaskRoutine.failure(error))
  }
}

function* rejectTask({ payload }) {
  yield put(rejectTaskRoutine.request())
  try {
    if (getIsOnline()) {
      const result = yield call(taskService.rejectTask, payload)
      yield call(() => db.tasks.put(result.data.data))
      yield put(rejectTaskRoutine.success(result.data.data))
      yield put(updateTaskRoutine.success(result.data.data))
      yield put(getProjectsRoutine())
      toast.success('Task rejected')
    } else {
      const existing = yield call(() => db.tasks.get(payload.id))
      const updated = { ...existing, status: 'rejected', _offline: true, updatedAt: new Date().toISOString() }
      yield call(() => db.tasks.put(updated))
      yield call(enqueueSyncAction, { type: 'REJECT_TASK', payload })
      yield put(rejectTaskRoutine.success(updated))
      yield put(updateTaskRoutine.success(updated))
      yield put(updateSyncCountsRoutine())
      toast.info('Task rejected offline.')
    }
  } catch (error) {
    if (isNetworkError(error)) {
      try {
        const existing = yield call(() => db.tasks.get(payload.id))
        const updated = { ...existing, status: 'rejected', _offline: true, updatedAt: new Date().toISOString() }
        yield call(() => db.tasks.put(updated))
        yield call(enqueueSyncAction, { type: 'REJECT_TASK', payload })
        yield put(rejectTaskRoutine.success(updated))
        yield put(updateTaskRoutine.success(updated))
        yield put(updateSyncCountsRoutine())
        toast.info('Task rejected offline.')
        return
      } catch (e) { /* fall through */ }
    }
    const status = error?.response?.status
    if (status === 403) {
      toast.error('You do not have permission to reject this task')
    } else {
      showErrorIfOnline(error)
    }
    yield put(rejectTaskRoutine.failure(error))
  }
}

function* holdTask({ payload }) {
  yield put(holdTaskRoutine.request())
  try {
    if (getIsOnline()) {
      const result = yield call(taskService.holdTask, payload)
      yield call(() => db.tasks.put(result.data.data))
      yield put(holdTaskRoutine.success(result.data.data))
      yield put(updateTaskRoutine.success(result.data.data))
      yield put(getProjectsRoutine())
      toast.success('Task put on hold')
    } else {
      const existing = yield call(() => db.tasks.get(payload.id))
      const updated = { ...existing, status: 'on_hold', _offline: true, updatedAt: new Date().toISOString() }
      yield call(() => db.tasks.put(updated))
      yield call(enqueueSyncAction, { type: 'HOLD_TASK', payload })
      yield put(holdTaskRoutine.success(updated))
      yield put(updateTaskRoutine.success(updated))
      yield put(updateSyncCountsRoutine())
      toast.info('Task put on hold offline.')
    }
  } catch (error) {
    if (isNetworkError(error)) {
      try {
        const existing = yield call(() => db.tasks.get(payload.id))
        const updated = { ...existing, status: 'on_hold', _offline: true, updatedAt: new Date().toISOString() }
        yield call(() => db.tasks.put(updated))
        yield call(enqueueSyncAction, { type: 'HOLD_TASK', payload })
        yield put(holdTaskRoutine.success(updated))
        yield put(updateTaskRoutine.success(updated))
        yield put(updateSyncCountsRoutine())
        toast.info('Task put on hold offline.')
        return
      } catch (e) { /* fall through */ }
    }
    const status = error?.response?.status
    if (status === 403) {
      toast.error('You do not have permission to put this task on hold')
    } else {
      showErrorIfOnline(error)
    }
    yield put(holdTaskRoutine.failure(error))
  }
}

function* resumeTask({ payload }) {
  yield put(resumeTaskRoutine.request())
  try {
    if (getIsOnline()) {
      const result = yield call(taskService.resumeTask, payload)
      yield call(() => db.tasks.put(result.data.data))
      yield put(resumeTaskRoutine.success(result.data.data))
      yield put(updateTaskRoutine.success(result.data.data))
      yield put(getProjectsRoutine())
      toast.success('Task resumed')
    } else {
      const existing = yield call(() => db.tasks.get(payload))
      const updated = { ...existing, status: 'open', _offline: true, updatedAt: new Date().toISOString() }
      yield call(() => db.tasks.put(updated))
      yield call(enqueueSyncAction, { type: 'RESUME_TASK', payload })
      yield put(resumeTaskRoutine.success(updated))
      yield put(updateTaskRoutine.success(updated))
      yield put(updateSyncCountsRoutine())
      toast.info('Task resumed offline.')
    }
  } catch (error) {
    if (isNetworkError(error)) {
      try {
        const existing = yield call(() => db.tasks.get(payload))
        const updated = { ...existing, status: 'open', _offline: true, updatedAt: new Date().toISOString() }
        yield call(() => db.tasks.put(updated))
        yield call(enqueueSyncAction, { type: 'RESUME_TASK', payload })
        yield put(resumeTaskRoutine.success(updated))
        yield put(updateTaskRoutine.success(updated))
        yield put(updateSyncCountsRoutine())
        toast.info('Task resumed offline.')
        return
      } catch (e) { /* fall through */ }
    }
    const status = error?.response?.status
    if (status === 403) {
      toast.error('You do not have permission to resume this task')
    } else {
      showErrorIfOnline(error)
    }
    yield put(resumeTaskRoutine.failure(error))
  }
}

function* signOffTask({ payload }) {
  yield put(signOffTaskRoutine.request())
  try {
    if (getIsOnline()) {
      const result = yield call(taskService.signOffTask, payload)
      yield call(() => db.tasks.put(result.data.data))
      yield put(signOffTaskRoutine.success(result.data.data))
      yield put(updateTaskRoutine.success(result.data.data))
      yield put(getProjectsRoutine())
      toast.success('Task signed off')
    } else {
      const existing = yield call(() => db.tasks.get(payload.id))
      const updated = { ...existing, status: 'signed_off', _offline: true, updatedAt: new Date().toISOString() }
      yield call(() => db.tasks.put(updated))
      yield call(enqueueSyncAction, { type: 'SIGN_OFF_TASK', payload })
      yield put(signOffTaskRoutine.success(updated))
      yield put(updateTaskRoutine.success(updated))
      yield put(updateSyncCountsRoutine())
      toast.info('Task signed off offline.')
    }
  } catch (error) {
    if (isNetworkError(error)) {
      try {
        const existing = yield call(() => db.tasks.get(payload.id))
        const updated = { ...existing, status: 'signed_off', _offline: true, updatedAt: new Date().toISOString() }
        yield call(() => db.tasks.put(updated))
        yield call(enqueueSyncAction, { type: 'SIGN_OFF_TASK', payload })
        yield put(signOffTaskRoutine.success(updated))
        yield put(updateTaskRoutine.success(updated))
        yield put(updateSyncCountsRoutine())
        toast.info('Task signed off offline.')
        return
      } catch (e) { /* fall through */ }
    }
    const status = error?.response?.status
    if (status === 403) {
      toast.error('You do not have permission to sign off this task')
    } else {
      showErrorIfOnline(error)
    }
    yield put(signOffTaskRoutine.failure(error))
  }
}

function* bulkSignOff({ payload }) {
  yield put(bulkSignOffRoutine.request())
  if (!getIsOnline()) {
    toast.warning('Bulk sign-off is not available offline. Please connect to the internet.')
    yield put(bulkSignOffRoutine.failure(new Error('Offline')))
    return
  }
  try {
    yield call(taskService.bulkSignOffTasks, payload)
    yield put(bulkSignOffRoutine.success())
    yield put(clearSelectionRoutine.success())
    yield put(getTasksRoutine())
    yield put(getProjectsRoutine())
    toast.success('Tasks signed off successfully')
  } catch (error) {
    if (isNetworkError(error)) {
      toast.warning('Bulk sign-off is not available offline. Please connect to the internet.')
    } else {
      showErrorIfOnline(error)
    }
    yield put(bulkSignOffRoutine.failure(error))
  }
}

function* getTaskSettings({ payload }) {
  yield put(getTaskSettingsRoutine.request())
  try {
    if (!getIsOnline()) {
      yield put(getTaskSettingsRoutine.success({}))
      return
    }
    const result = yield call(taskService.getTaskSettings, payload)
    yield put(getTaskSettingsRoutine.success(result.data.data))
  } catch (error) {
    if (error?.message === 'Network Error' || error?.code === 'ERR_NETWORK') {
      yield put(getTaskSettingsRoutine.success({}))
      return
    }
    yield put(getTaskSettingsRoutine.failure(error))
  }
}

function* updateTaskSettings({ payload }) {
  yield put(updateTaskSettingsRoutine.request())
  try {
    const result = yield call(taskService.updateTaskSettings, payload)
    yield put(updateTaskSettingsRoutine.success(result.data.data))
    yield put(getTaskStatsRoutine())
    toast.success('Task settings updated')
  } catch (error) {
    showErrorIfOnline(error)
    yield put(updateTaskSettingsRoutine.failure(error))
  }
}

function* getProjectDashboard({ payload }) {
  yield put(getProjectDashboardRoutine.request())
  try {
    const params = {}
    if (payload?.buildingPassportId) {
      params.building_passport_id = payload.buildingPassportId
    }
    const result = yield call(taskService.getProjectDashboard, params)
    yield put(getProjectDashboardRoutine.success(result.data.data))
  } catch (error) {
    if (error?.message === 'Network Error' || error?.code === 'ERR_NETWORK') {
      yield put(getProjectDashboardRoutine.success(null))
      return
    }
    yield put(getProjectDashboardRoutine.failure(error))
  }
}

function* starTask({ payload }) {
  yield put(starTaskRoutine.request())
  if (!getIsOnline()) {
    toast.warning('Starring is not available offline.')
    yield put(starTaskRoutine.failure(new Error('Offline')))
    return
  }
  try {
    const result = yield call(taskService.toggleStarTask, payload)
    yield put(starTaskRoutine.success(result.data.data))
    yield put(updateTaskRoutine.success(result.data.data))
  } catch (error) {
    if (isNetworkError(error)) {
      toast.warning('Starring is not available offline.')
    } else {
      showErrorIfOnline(error)
    }
    yield put(starTaskRoutine.failure(error))
  }
}

function* syncBlockers({ payload }) {
  yield put(syncBlockersRoutine.request())
  if (!getIsOnline()) {
    toast.warning('Updating dependencies is not available offline. Please connect to the internet.')
    yield put(syncBlockersRoutine.failure(new Error('Offline')))
    return
  }
  try {
    const result = yield call(taskService.syncBlockers, payload)
    yield put(syncBlockersRoutine.success(result.data.data))
    yield put(updateTaskRoutine.success(result.data.data))
    toast.success('Task dependencies updated')
  } catch (error) {
    showErrorIfOnline(error)
    yield put(syncBlockersRoutine.failure(error))
  }
}

function* assignExternalUser({ payload }) {
  yield put(assignExternalUserRoutine.request())
  try {
    const result = yield call(taskService.assignExternalUser, payload)
    yield put(assignExternalUserRoutine.success(result.data.data))
    yield put(updateTaskRoutine.success(result.data.data))
    toast.success('External user invited')
  } catch (error) {
    showErrorIfOnline(error)
    yield put(assignExternalUserRoutine.failure(error))
  }
}

function* removeExternalAssignee({ payload }) {
  yield put(removeExternalAssigneeRoutine.request())
  try {
    yield call(taskService.removeExternalAssignee, payload)
    yield put(removeExternalAssigneeRoutine.success(payload))
    // Reload the task to get updated data
    yield put(getTaskRoutine(payload.taskId))
    toast.success('External assignee removed')
  } catch (error) {
    showErrorIfOnline(error)
    yield put(removeExternalAssigneeRoutine.failure(error))
  }
}

// Projects
function* getProjects({ payload }) {
  yield put(getProjectsRoutine.request())
  try {
    if (getIsOnline()) {
      const result = yield call(taskService.getProjects, payload)
      const projects = result.data.data
      if (projects?.length > 0) yield call(() => db.projects.bulkPut(projects))
      yield put(getProjectsRoutine.success(projects))
    } else {
      const projects = yield call(() => db.projects.toArray())
      yield put(getProjectsRoutine.success(projects))
    }
  } catch (error) {
    if (error?.message === 'Network Error' || error?.code === 'ERR_NETWORK') {
      try { const p = yield call(() => db.projects.toArray()); yield put(getProjectsRoutine.success(p)); return } catch(e) {}
    }
    showErrorIfOnline(error)
    yield put(getProjectsRoutine.failure(error))
  }
}

function* createProject({ payload }) {
  yield put(createProjectRoutine.request())
  try {
    const result = yield call(taskService.createProject, payload)
    yield put(createProjectRoutine.success(result.data.data))
    toast.success('Project created successfully')
  } catch (error) {
    showErrorIfOnline(error)
    yield put(createProjectRoutine.failure(error))
  }
}

function* updateProject({ payload }) {
  yield put(updateProjectRoutine.request())
  try {
    const result = yield call(taskService.updateProject, payload)
    yield put(updateProjectRoutine.success(result.data.data))
    toast.success('Project updated successfully')
  } catch (error) {
    showErrorIfOnline(error)
    yield put(updateProjectRoutine.failure(error))
  }
}

function* deleteProject({ payload }) {
  yield put(deleteProjectRoutine.request())
  try {
    yield call(taskService.deleteProject, payload)
    yield put(deleteProjectRoutine.success(payload))
    toast.success('Project deleted successfully')
  } catch (error) {
    showErrorIfOnline(error)
    yield put(deleteProjectRoutine.failure(error))
  }
}

// Form Templates
function* getFormTemplates({ payload }) {
  yield put(getFormTemplatesRoutine.request())
  try {
    if (getIsOnline()) {
      const result = yield call(taskService.getFormTemplates, { limit: 200, ...payload })
      const templates = result.data.data
      if (templates?.length > 0) yield call(() => db.formTemplates.bulkPut(templates))
      yield put(getFormTemplatesRoutine.success(templates))
    } else {
      const templates = yield call(() => db.formTemplates.toArray())
      yield put(getFormTemplatesRoutine.success(templates))
    }
  } catch (error) {
    if (error?.message === 'Network Error' || error?.code === 'ERR_NETWORK') {
      try { const t = yield call(() => db.formTemplates.toArray()); yield put(getFormTemplatesRoutine.success(t)); return } catch(e) {}
    }
    showErrorIfOnline(error)
    yield put(getFormTemplatesRoutine.failure(error))
  }
}

function* getFormTemplate({ payload }) {
  yield put(getFormTemplateRoutine.request())
  try {
    const result = yield call(taskService.getFormTemplate, payload)
    yield put(getFormTemplateRoutine.success(result.data.data))
  } catch (error) {
    showErrorIfOnline(error)
    yield put(getFormTemplateRoutine.failure(error))
  }
}

function* createFormTemplate({ payload }) {
  const { callback, ...data } = payload
  yield put(createFormTemplateRoutine.request())
  try {
    const result = yield call(taskService.createFormTemplate, data)
    yield put(createFormTemplateRoutine.success(result.data.data))
    invalidateFormTemplatesCache()
    toast.success('Form template created successfully')
    if (callback) callback()
  } catch (error) {
    showErrorIfOnline(error)
    yield put(createFormTemplateRoutine.failure(error))
  }
}

function* updateFormTemplate({ payload }) {
  const { callback, ...data } = payload
  yield put(updateFormTemplateRoutine.request())
  try {
    const result = yield call(taskService.updateFormTemplate, data)
    yield put(updateFormTemplateRoutine.success(result.data.data))
    invalidateFormTemplatesCache()
    toast.success('Form template updated successfully')
    if (callback) callback()
  } catch (error) {
    showErrorIfOnline(error)
    yield put(updateFormTemplateRoutine.failure(error))
  }
}

function* deleteFormTemplate({ payload }) {
  yield put(deleteFormTemplateRoutine.request())
  try {
    yield call(taskService.deleteFormTemplate, payload)
    yield put(deleteFormTemplateRoutine.success(payload))
    invalidateFormTemplatesCache()
    toast.success('Form template deleted')
  } catch (error) {
    showErrorIfOnline(error)
    yield put(deleteFormTemplateRoutine.failure(error))
  }
}

function* publishFormTemplate({ payload }) {
  yield put(publishFormTemplateRoutine.request())
  try {
    const result = yield call(taskService.publishFormTemplate, payload)
    yield put(publishFormTemplateRoutine.success(result.data.data))
    invalidateFormTemplatesCache()
    toast.success('Form template published')
  } catch (error) {
    showErrorIfOnline(error)
    yield put(publishFormTemplateRoutine.failure(error))
  }
}

function* cloneFormTemplate({ payload }) {
  yield put(cloneFormTemplateRoutine.request())
  try {
    const result = yield call(taskService.cloneFormTemplate, payload)
    yield put(cloneFormTemplateRoutine.success(result.data.data))
    invalidateFormTemplatesCache()
    toast.success('Form template cloned')
  } catch (error) {
    showErrorIfOnline(error)
    yield put(cloneFormTemplateRoutine.failure(error))
  }
}

// Template Assignments
function* getTemplateAssignments({ payload }) {
  yield put(getTemplateAssignmentsRoutine.request())
  try {
    const result = yield call(taskService.getTemplateAssignments, payload)
    yield put(getTemplateAssignmentsRoutine.success(result.data.data))
  } catch (error) {
    showErrorIfOnline(error)
    yield put(getTemplateAssignmentsRoutine.failure(error))
  }
}

function* createTemplateAssignment({ payload }) {
  const { onSuccess, onError, ...rest } = payload
  yield put(createTemplateAssignmentRoutine.request())
  try {
    const result = yield call(taskService.createTemplateAssignment, rest)
    yield put(createTemplateAssignmentRoutine.success(result.data.data))
    toast.success('Assignment created')
    if (onSuccess) onSuccess()
  } catch (error) {
    showErrorIfOnline(error)
    yield put(createTemplateAssignmentRoutine.failure(error))
    if (onError) onError()
  }
}

function* deleteTemplateAssignment({ payload }) {
  yield put(deleteTemplateAssignmentRoutine.request())
  try {
    yield call(taskService.deleteTemplateAssignment, payload)
    yield put(deleteTemplateAssignmentRoutine.success(payload.assignmentId))
    toast.success('Assignment removed')
  } catch (error) {
    showErrorIfOnline(error)
    yield put(deleteTemplateAssignmentRoutine.failure(error))
  }
}

// Form Responses
function* saveFormResponse({ payload }) {
  yield put(saveFormResponseRoutine.request())
  // Auto-saves are draft persistence for a single user — the OCC check is
  // meant to protect concurrent editing by *different* users, and applying
  // it to background auto-saves produces spurious "updated by someone else"
  // toasts whenever takeLatest cancels a saga whose HTTP request had
  // already left the browser (the request still increments current_version
  // server-side, but the cancelled saga never dispatches success, so the
  // store stays a version behind).
  const effectivePayload = payload.autoSave
    ? { ...payload, currentVersion: null }
    : payload
  try {
    if (getIsOnline()) {
      const result = yield call(taskService.saveFormResponse, effectivePayload)
      yield call(() => db.tasks.put(result.data.data))
      yield put(saveFormResponseRoutine.success(result.data.data))
      yield put(updateTaskRoutine.success(result.data.data))
      if (!payload.autoSave) {
        toast.success('Form response saved')
      }
    } else {
      const existing = yield call(() => db.tasks.get(payload.taskId))
      // Keep the server's currentVersion — don't increment locally.
      // The server manages versioning; local increment causes 409 on sync.
      const serverVersion = existing?.formResponse?.currentVersion || payload.currentVersion || 0
      const updated = {
        ...existing,
        formResponse: { ...existing?.formResponse, responses: payload.responses, currentVersion: serverVersion },
        ...(['assigned', 'unassigned', 'pending_dependent'].includes(existing?.status) ? { status: 'in_progress' } : {}),
        _offline: true,
        updatedAt: new Date().toISOString()
      }
      yield call(() => db.tasks.put(updated))
      yield call(deduplicateSyncEntry, 'SAVE_FORM_RESPONSE', payload.taskId)
      // Always use the server's version in the sync payload
      yield call(enqueueSyncAction, { type: 'SAVE_FORM_RESPONSE', payload: { ...payload, currentVersion: serverVersion } })
      yield put(saveFormResponseRoutine.success(updated))
      yield put(updateTaskRoutine.success(updated))
      yield put(updateSyncCountsRoutine())
      if (!payload.autoSave) {
        toast.info('Form saved offline. Will sync when connected.')
      }
    }
  } catch (error) {
    if (isNetworkError(error)) {
      try {
        const existing = yield call(() => db.tasks.get(payload.taskId))
        const serverVersion = existing?.formResponse?.currentVersion || payload.currentVersion || 0
        const updated = {
          ...existing,
          formResponse: { ...existing?.formResponse, responses: payload.responses, currentVersion: serverVersion },
          ...(['assigned', 'unassigned', 'pending_dependent'].includes(existing?.status) ? { status: 'in_progress' } : {}),
          _offline: true,
          updatedAt: new Date().toISOString()
        }
        yield call(() => db.tasks.put(updated))
        yield call(deduplicateSyncEntry, 'SAVE_FORM_RESPONSE', payload.taskId)
        yield call(enqueueSyncAction, { type: 'SAVE_FORM_RESPONSE', payload: { ...payload, currentVersion: serverVersion } })
        yield put(saveFormResponseRoutine.success(updated))
        yield put(updateTaskRoutine.success(updated))
        yield put(updateSyncCountsRoutine())
        if (!payload.autoSave) toast.info('Form saved offline. Will sync when connected.')
        return
      } catch (e) { /* fall through */ }
    }
    if (error?.response?.status === 409) {
      // The server tells us the actual current_version in the 409 body —
      // use it to silently retry once before bothering the user. Most 409s
      // here are races between concurrent saves by the same user (a save
      // request still in flight, store hasn't picked up the new version
      // yet), not a different user editing.
      const serverVersion = error?.response?.data?.currentVersion ?? error?.response?.data?.current_version
      if (!payload._retried409 && serverVersion != null) {
        try {
          const retryResult = yield call(taskService.saveFormResponse, {
            ...payload,
            currentVersion: serverVersion,
          })
          yield call(() => db.tasks.put(retryResult.data.data))
          yield put(saveFormResponseRoutine.success(retryResult.data.data))
          yield put(updateTaskRoutine.success(retryResult.data.data))
          if (!payload.autoSave) toast.success('Form response saved')
          return
        } catch (retryError) {
          if (retryError?.response?.status === 409) {
            toast.warning('This form was updated by someone else. Refreshing...')
            yield put(getTaskRoutine(payload.taskId))
          } else {
            showErrorIfOnline(retryError)
          }
          yield put(saveFormResponseRoutine.failure(retryError))
          return
        }
      }
      toast.warning('This form was updated by someone else. Refreshing...')
      yield put(getTaskRoutine(payload.taskId))
    } else {
      showErrorIfOnline(error)
    }
    yield put(saveFormResponseRoutine.failure(error))
  }
}

function* saveApproverFormResponse({ payload }) {
  yield put(saveApproverFormResponseRoutine.request())
  try {
    if (getIsOnline()) {
      const result = yield call(taskService.saveApproverFormResponse, payload)
      yield put(saveApproverFormResponseRoutine.success(result.data.data))
      yield put(updateTaskRoutine.success(result.data.data))
      toast.success('Approver edits saved')
    } else {
      const existing = yield call(() => db.tasks.get(payload.taskId))
      const updated = {
        ...existing,
        formResponse: { ...existing?.formResponse, responses: payload.responses, currentVersion: existing?.formResponse?.currentVersion || payload.currentVersion || 0 },
        ...(['assigned', 'unassigned', 'pending_dependent'].includes(existing?.status) ? { status: 'in_progress' } : {}),
        _offline: true,
        updatedAt: new Date().toISOString()
      }
      yield call(() => db.tasks.put(updated))
      yield call(deduplicateSyncEntry, 'SAVE_FORM_RESPONSE', payload.taskId)
      const serverVersion = existing?.formResponse?.currentVersion || payload.currentVersion || 0
      yield call(enqueueSyncAction, { type: 'SAVE_FORM_RESPONSE', payload: { ...payload, currentVersion: serverVersion } })
      yield put(saveApproverFormResponseRoutine.success(updated))
      yield put(updateTaskRoutine.success(updated))
      yield put(updateSyncCountsRoutine())
      toast.info('Approver edits saved offline. Will sync when connected.')
    }
  } catch (error) {
    if (isNetworkError(error)) {
      try {
        const existing = yield call(() => db.tasks.get(payload.taskId))
        const svrVer = existing?.formResponse?.currentVersion || payload.currentVersion || 0
        const updated = {
          ...existing,
          formResponse: { ...existing?.formResponse, responses: payload.responses, currentVersion: svrVer },
          ...(['assigned', 'unassigned', 'pending_dependent'].includes(existing?.status) ? { status: 'in_progress' } : {}),
          _offline: true,
          updatedAt: new Date().toISOString()
        }
        yield call(() => db.tasks.put(updated))
        yield call(deduplicateSyncEntry, 'SAVE_FORM_RESPONSE', payload.taskId)
        yield call(enqueueSyncAction, { type: 'SAVE_FORM_RESPONSE', payload: { ...payload, currentVersion: svrVer } })
        yield put(saveApproverFormResponseRoutine.success(updated))
        yield put(updateTaskRoutine.success(updated))
        yield put(updateSyncCountsRoutine())
        toast.info('Approver edits saved offline. Will sync when connected.')
        return
      } catch (e) { /* fall through */ }
    }
    if (error?.response?.status === 409) {
      const serverVersion = error?.response?.data?.currentVersion ?? error?.response?.data?.current_version
      if (!payload._retried409 && serverVersion != null) {
        try {
          const retryResult = yield call(taskService.saveApproverFormResponse, {
            ...payload,
            currentVersion: serverVersion,
          })
          yield put(saveApproverFormResponseRoutine.success(retryResult.data.data))
          yield put(updateTaskRoutine.success(retryResult.data.data))
          toast.success('Approver edits saved')
          return
        } catch (retryError) {
          if (retryError?.response?.status === 409) {
            toast.warning('This form was updated by someone else. Refreshing...')
            yield put(getTaskRoutine(payload.taskId))
          } else {
            showErrorIfOnline(retryError)
          }
          yield put(saveApproverFormResponseRoutine.failure(retryError))
          return
        }
      }
      toast.warning('This form was updated by someone else. Refreshing...')
      yield put(getTaskRoutine(payload.taskId))
    } else {
      showErrorIfOnline(error)
    }
    yield put(saveApproverFormResponseRoutine.failure(error))
  }
}

function* getFormVersions({ payload }) {
  yield put(getFormVersionsRoutine.request())
  try {
    const result = yield call(taskService.getFormVersions, payload)
    yield put(getFormVersionsRoutine.success(result.data.data))
  } catch (error) {
    showErrorIfOnline(error)
    yield put(getFormVersionsRoutine.failure(error))
  }
}

function* getConflicts({ payload }) {
  yield put(getConflictsRoutine.request())
  try {
    const result = yield call(taskService.getConflicts, payload)
    yield put(getConflictsRoutine.success(result.data.data))
  } catch (error) {
    showErrorIfOnline(error)
    yield put(getConflictsRoutine.failure(error))
  }
}

// Teams
function* getTeams({ payload }) {
  yield put(getTeamsRoutine.request())
  try {
    if (getIsOnline()) {
      const result = yield call(taskService.getTeams, payload)
      const teams = result.data.data
      if (teams?.length > 0) yield call(() => db.teams.bulkPut(teams))
      yield put(getTeamsRoutine.success(teams))
    } else {
      const teams = yield call(() => db.teams.toArray())
      yield put(getTeamsRoutine.success(teams))
    }
  } catch (error) {
    if (error?.message === 'Network Error' || error?.code === 'ERR_NETWORK') {
      try { const t = yield call(() => db.teams.toArray()); yield put(getTeamsRoutine.success(t)); return } catch(e) {}
    }
    showErrorIfOnline(error)
    yield put(getTeamsRoutine.failure(error))
  }
}

function* createTeam({ payload }) {
  yield put(createTeamRoutine.request())
  try {
    const result = yield call(taskService.createTeam, payload)
    yield put(createTeamRoutine.success(result.data.data))
    toast.success('Team created successfully')
  } catch (error) {
    showErrorIfOnline(error)
    yield put(createTeamRoutine.failure(error))
  }
}

function* updateTeam({ payload }) {
  yield put(updateTeamRoutine.request())
  try {
    const result = yield call(taskService.updateTeam, payload)
    yield put(updateTeamRoutine.success(result.data.data))
    toast.success('Team updated successfully')
  } catch (error) {
    showErrorIfOnline(error)
    yield put(updateTeamRoutine.failure(error))
  }
}

function* deleteTeam({ payload }) {
  yield put(deleteTeamRoutine.request())
  try {
    yield call(taskService.deleteTeam, payload)
    yield put(deleteTeamRoutine.success(payload))
    toast.success('Team deleted successfully')
  } catch (error) {
    showErrorIfOnline(error)
    yield put(deleteTeamRoutine.failure(error))
  }
}

function* addTeamMember({ payload }) {
  yield put(addTeamMemberRoutine.request())
  try {
    yield call(taskService.addTeamMember, payload)
    yield put(addTeamMemberRoutine.success())
    yield put(getTeamsRoutine())
    toast.success('Team member added')
  } catch (error) {
    showErrorIfOnline(error)
    yield put(addTeamMemberRoutine.failure(error))
  }
}

function* removeTeamMember({ payload }) {
  yield put(removeTeamMemberRoutine.request())
  try {
    yield call(taskService.removeTeamMember, payload)
    yield put(removeTeamMemberRoutine.success())
    yield put(getTeamsRoutine())
    toast.success('Team member removed')
  } catch (error) {
    showErrorIfOnline(error)
    yield put(removeTeamMemberRoutine.failure(error))
  }
}

// Bulk Operations
function* bulkDeleteTasks({ payload }) {
  yield put(bulkDeleteTasksRoutine.request())
  try {
    const result = yield call(taskService.bulkDeleteTasks, payload.taskIds)
    const count = result.data?.deletedCount || result.data?.deleted_count
    yield put(bulkDeleteTasksRoutine.success())
    yield put(clearSelectionRoutine.success())
    yield put(getTasksRoutine())
    yield put(getProjectsRoutine())
    if (count !== undefined && count < payload.taskIds.length) {
      toast.warning(`Deleted ${count} of ${payload.taskIds.length} tasks (some may lack permission)`)
    } else {
      toast.success('Tasks deleted successfully')
    }
  } catch (error) {
    showErrorIfOnline(error)
    yield put(bulkDeleteTasksRoutine.failure(error))
  }
}

function* bulkUpdateTasks({ payload }) {
  yield put(bulkUpdateTasksRoutine.request())
  try {
    const result = yield call(taskService.bulkUpdateTasks, payload)
    const count = result.data?.updatedCount ?? result.data?.updated_count
    const skippedObligation = result.data?.skippedObligationCount ?? result.data?.skipped_obligation_count ?? 0
    yield put(bulkUpdateTasksRoutine.success())
    yield put(clearSelectionRoutine.success())
    yield put(getTasksRoutine())
    yield put(getProjectsRoutine())
    if (skippedObligation > 0) {
      toast.info(
        `Updated ${count} of ${payload.taskIds.length} tasks. ${skippedObligation} obligation-linked task${skippedObligation === 1 ? '' : 's'} skipped. Their form is set by the compliance obligation and can't be changed here.`
      )
    } else if (count !== undefined && count < payload.taskIds.length) {
      toast.warning(`Updated ${count} of ${payload.taskIds.length} tasks (some may lack permission)`)
    } else {
      toast.success('Tasks updated successfully')
    }
  } catch (error) {
    showErrorIfOnline(error)
    yield put(bulkUpdateTasksRoutine.failure(error))
  }
}

function* bulkAssignTasks({ payload }) {
  yield put(bulkAssignTasksRoutine.request())
  try {
    const result = yield call(taskService.bulkAssignTasks, payload)
    const count = result.data?.updatedCount || result.data?.updated_count
    yield put(bulkAssignTasksRoutine.success())
    yield put(clearSelectionRoutine.success())
    yield put(getTasksRoutine())
    yield put(getProjectsRoutine())
    if (count !== undefined && count < payload.taskIds.length) {
      toast.warning(`Assigned ${count} of ${payload.taskIds.length} tasks (some may lack permission)`)
    } else {
      toast.success('Tasks assigned successfully')
    }
  } catch (error) {
    showErrorIfOnline(error)
    yield put(bulkAssignTasksRoutine.failure(error))
  }
}

// Selection
function* selectTask({ payload }) {
  yield put(selectTaskRoutine.success(payload))
}

function* selectAllTasks({ payload }) {
  // If payload is an array (local toggle from current page), use it directly
  if (Array.isArray(payload)) {
    yield put(selectAllTasksRoutine.success(payload))
    return
  }
  // If payload requests server-side select all, fetch all matching IDs
  if (payload?.selectAll) {
    try {
      const filters = yield select(selectTaskFilters)
      const params = buildTaskParams(filters)
      delete params.page
      delete params.limit
      const result = yield call(taskService.selectAllTaskIds, params)
      yield put(selectAllTasksRoutine.success(result.data.data))
    } catch (error) {
      showErrorIfOnline(error)
      yield put(selectAllTasksRoutine.success([]))
    }
    return
  }
  yield put(selectAllTasksRoutine.success(payload || []))
}

function* clearSelection() {
  yield put(clearSelectionRoutine.success())
}

// Filters
export function* setTaskFilters({ payload }) {
  yield put(setTaskFiltersRoutine.success(payload))
  yield put(clearSelectionRoutine.success())
  yield put(getTasksRoutine())
  const filters = yield select(selectTaskFilters)
  if (filters.category) yield call(refreshTaskCounts)
}

function* clearTaskFilters() {
  yield put(clearTaskFiltersRoutine.success())
  yield put(getTasksRoutine())
}

// --- Watchers ---

const TASK_STATS_STALE_ON = [
  createTaskRoutine.SUCCESS,
  replicateTaskRoutine.SUCCESS,
  deleteTaskRoutine.SUCCESS,
  updateTaskRoutine.SUCCESS,
  bulkSignOffRoutine.SUCCESS,
  bulkDeleteTasksRoutine.SUCCESS,
  bulkUpdateTasksRoutine.SUCCESS,
  bulkAssignTasksRoutine.SUCCESS
]

export function* refreshTaskCounts() {
  if (!getIsOnline()) return
  const filters = yield select(selectTaskFilters)
  try {
    const listResult = yield call(taskService.getTasks, { ...buildTaskParams(filters, 1), limit: 1 })
    const { meta, windows } = listResult?.data || {}
    let viewCounts = listResult?.data?.viewCounts
    if (filters.category) {
      const countsResult = yield call(taskService.getTasks, { ...buildTaskParams({ ...filters, category: null }, 1), limit: 1 })
      viewCounts = countsResult?.data?.viewCounts
    }
    yield put(refreshTaskCountsRoutine.success({ windows, viewCounts, total: meta?.total, queryKey: taskQueryKey(filters) }))
  } catch (error) {
    yield put(refreshTaskCountsRoutine.failure(error))
  }
}

export function* refreshTaskStatsAfterMutation() {
  yield delay(400)
  const tm = yield select(state => state.taskManagement || {})
  if (tm.taskStats) yield put(getTaskStatsRoutine())
  if (tm.windowCounts || tm.viewCounts) yield call(refreshTaskCounts)
}

export function* taskStatsFreshnessWatcher() {
  yield takeLatest(TASK_STATS_STALE_ON, refreshTaskStatsAfterMutation)
}

function* getTaskStatsWatcher() {
  yield takeLatest(getTaskStatsRoutine.TRIGGER, getTaskStats)
}
const DUPLICATE_REQUEST_MS = 750

export const getTasksRequestKey = (filters, payload) => JSON.stringify([
  taskQueryKey(filters),
  payload?.page || 1,
  !!payload?.stable,
  !!payload?.fromCache
])

export function* getTasksWatcher() {
  let running = null
  let runningKey = null
  let startedAt = 0
  while (true) {
    const action = yield take(getTasksRoutine.TRIGGER)
    const filters = yield select(selectTaskFilters)
    const key = getTasksRequestKey(filters, action.payload)
    const busy = running && running.isRunning()
    if (busy && key === runningKey && Date.now() - startedAt < DUPLICATE_REQUEST_MS) continue
    if (busy) yield cancel(running)
    runningKey = key
    startedAt = Date.now()
    running = yield fork(getTasks, action)
  }
}
function* getTaskWatcher() {
  yield takeLatest(getTaskRoutine.TRIGGER, getTask)
}
function* createTaskWatcher() {
  yield takeEvery(createTaskRoutine.TRIGGER, createTask)
}
function* replicateTaskWatcher() {
  yield takeEvery(replicateTaskRoutine.TRIGGER, replicateTask)
}
export function* updateTaskWatcher() {
  yield takeEvery(updateTaskRoutine.TRIGGER, updateTask)
}
export function* deleteTaskWatcher() {
  yield takeEvery(deleteTaskRoutine.TRIGGER, deleteTask)
}
function* assignTaskWatcher() {
  yield takeEvery(assignTaskRoutine.TRIGGER, assignTask)
}
function* updateApproversWatcher() {
  yield takeEvery(updateApproversRoutine.TRIGGER, updateApprovers)
}
function* submitTaskWatcher() {
  yield takeEvery(submitTaskRoutine.TRIGGER, submitTask)
}
function* saveAndSubmitTaskWatcher() {
  yield takeEvery(saveAndSubmitTaskRoutine.TRIGGER, saveAndSubmitTask)
}
function* approveTaskWatcher() {
  yield takeEvery(approveTaskRoutine.TRIGGER, approveTask)
}
function* rejectTaskWatcher() {
  yield takeEvery(rejectTaskRoutine.TRIGGER, rejectTask)
}
function* holdTaskWatcher() {
  yield takeEvery(holdTaskRoutine.TRIGGER, holdTask)
}
function* resumeTaskWatcher() {
  yield takeEvery(resumeTaskRoutine.TRIGGER, resumeTask)
}
function* signOffTaskWatcher() {
  yield takeEvery(signOffTaskRoutine.TRIGGER, signOffTask)
}
function* bulkSignOffWatcher() {
  yield takeLatest(bulkSignOffRoutine.TRIGGER, bulkSignOff)
}
function* getTaskSettingsWatcher() {
  yield takeLatest(getTaskSettingsRoutine.TRIGGER, getTaskSettings)
}
function* updateTaskSettingsWatcher() {
  yield takeLatest(updateTaskSettingsRoutine.TRIGGER, updateTaskSettings)
}
function* getProjectDashboardWatcher() {
  yield takeLatest(getProjectDashboardRoutine.TRIGGER, getProjectDashboard)
}
function* starTaskWatcher() {
  yield takeEvery(starTaskRoutine.TRIGGER, starTask)
}
function* syncBlockersWatcher() {
  yield takeEvery(syncBlockersRoutine.TRIGGER, syncBlockers)
}
function* assignExternalUserWatcher() {
  yield takeEvery(assignExternalUserRoutine.TRIGGER, assignExternalUser)
}
function* removeExternalAssigneeWatcher() {
  yield takeEvery(removeExternalAssigneeRoutine.TRIGGER, removeExternalAssignee)
}
function* getProjectsWatcher() {
  yield takeLatest(getProjectsRoutine.TRIGGER, getProjects)
}
function* createProjectWatcher() {
  yield takeLatest(createProjectRoutine.TRIGGER, createProject)
}
function* updateProjectWatcher() {
  yield takeLatest(updateProjectRoutine.TRIGGER, updateProject)
}
function* deleteProjectWatcher() {
  yield takeLatest(deleteProjectRoutine.TRIGGER, deleteProject)
}
function* getFormTemplatesWatcher() {
  yield takeLatest(getFormTemplatesRoutine.TRIGGER, getFormTemplates)
}
function* getFormTemplateWatcher() {
  yield takeLatest(getFormTemplateRoutine.TRIGGER, getFormTemplate)
}
function* createFormTemplateWatcher() {
  yield takeLatest(createFormTemplateRoutine.TRIGGER, createFormTemplate)
}
function* updateFormTemplateWatcher() {
  yield takeLatest(updateFormTemplateRoutine.TRIGGER, updateFormTemplate)
}
function* deleteFormTemplateWatcher() {
  yield takeLatest(deleteFormTemplateRoutine.TRIGGER, deleteFormTemplate)
}
function* publishFormTemplateWatcher() {
  yield takeLatest(publishFormTemplateRoutine.TRIGGER, publishFormTemplate)
}
function* cloneFormTemplateWatcher() {
  yield takeLatest(cloneFormTemplateRoutine.TRIGGER, cloneFormTemplate)
}
function* getTemplateAssignmentsWatcher() {
  yield takeLatest(getTemplateAssignmentsRoutine.TRIGGER, getTemplateAssignments)
}
function* createTemplateAssignmentWatcher() {
  yield takeLatest(createTemplateAssignmentRoutine.TRIGGER, createTemplateAssignment)
}
function* deleteTemplateAssignmentWatcher() {
  yield takeLatest(deleteTemplateAssignmentRoutine.TRIGGER, deleteTemplateAssignment)
}
function* saveFormResponseWatcher() {
  yield takeLatest(saveFormResponseRoutine.TRIGGER, saveFormResponse)
}
function* saveApproverFormResponseWatcher() {
  yield takeLatest(saveApproverFormResponseRoutine.TRIGGER, saveApproverFormResponse)
}
function* getFormVersionsWatcher() {
  yield takeLatest(getFormVersionsRoutine.TRIGGER, getFormVersions)
}
function* getConflictsWatcher() {
  yield takeLatest(getConflictsRoutine.TRIGGER, getConflicts)
}
function* getTeamsWatcher() {
  yield takeLatest(getTeamsRoutine.TRIGGER, getTeams)
}
function* createTeamWatcher() {
  yield takeLatest(createTeamRoutine.TRIGGER, createTeam)
}
function* updateTeamWatcher() {
  yield takeLatest(updateTeamRoutine.TRIGGER, updateTeam)
}
function* deleteTeamWatcher() {
  yield takeLatest(deleteTeamRoutine.TRIGGER, deleteTeam)
}
function* addTeamMemberWatcher() {
  yield takeLatest(addTeamMemberRoutine.TRIGGER, addTeamMember)
}
function* removeTeamMemberWatcher() {
  yield takeLatest(removeTeamMemberRoutine.TRIGGER, removeTeamMember)
}
function* bulkDeleteTasksWatcher() {
  yield takeLatest(bulkDeleteTasksRoutine.TRIGGER, bulkDeleteTasks)
}
function* bulkUpdateTasksWatcher() {
  yield takeLatest(bulkUpdateTasksRoutine.TRIGGER, bulkUpdateTasks)
}
function* bulkAssignTasksWatcher() {
  yield takeLatest(bulkAssignTasksRoutine.TRIGGER, bulkAssignTasks)
}
function* selectTaskWatcher() {
  yield takeLatest(selectTaskRoutine.TRIGGER, selectTask)
}
function* selectAllTasksWatcher() {
  yield takeLatest(selectAllTasksRoutine.TRIGGER, selectAllTasks)
}
function* clearSelectionWatcher() {
  yield takeLatest(clearSelectionRoutine.TRIGGER, clearSelection)
}
function* setTaskFiltersWatcher() {
  yield takeLatest(setTaskFiltersRoutine.TRIGGER, setTaskFilters)
}
function* clearTaskFiltersWatcher() {
  yield takeLatest(clearTaskFiltersRoutine.TRIGGER, clearTaskFilters)
}

// --- Exported sagas ---

export const taskManagementSagas = [
  fork(getTaskStatsWatcher),
  fork(taskStatsFreshnessWatcher),
  fork(getTasksWatcher),
  fork(getTaskWatcher),
  fork(createTaskWatcher),
  fork(updateTaskWatcher),
  fork(deleteTaskWatcher),
  fork(assignTaskWatcher),
  fork(updateApproversWatcher),
  fork(submitTaskWatcher),
  fork(saveAndSubmitTaskWatcher),
  fork(approveTaskWatcher),
  fork(rejectTaskWatcher),
  fork(starTaskWatcher),
  fork(syncBlockersWatcher),
  fork(assignExternalUserWatcher),
  fork(removeExternalAssigneeWatcher),
  fork(getProjectsWatcher),
  fork(createProjectWatcher),
  fork(updateProjectWatcher),
  fork(deleteProjectWatcher),
  fork(getFormTemplatesWatcher),
  fork(getFormTemplateWatcher),
  fork(createFormTemplateWatcher),
  fork(updateFormTemplateWatcher),
  fork(deleteFormTemplateWatcher),
  fork(publishFormTemplateWatcher),
  fork(cloneFormTemplateWatcher),
  fork(saveFormResponseWatcher),
  fork(saveApproverFormResponseWatcher),
  fork(getFormVersionsWatcher),
  fork(getConflictsWatcher),
  fork(bulkDeleteTasksWatcher),
  fork(bulkUpdateTasksWatcher),
  fork(bulkAssignTasksWatcher),
  fork(selectTaskWatcher),
  fork(selectAllTasksWatcher),
  fork(clearSelectionWatcher),
  fork(setTaskFiltersWatcher),
  fork(clearTaskFiltersWatcher),
  fork(getTeamsWatcher),
  fork(createTeamWatcher),
  fork(updateTeamWatcher),
  fork(deleteTeamWatcher),
  fork(addTeamMemberWatcher),
  fork(removeTeamMemberWatcher),
  fork(replicateTaskWatcher),
  fork(holdTaskWatcher),
  fork(resumeTaskWatcher),
  fork(signOffTaskWatcher),
  fork(bulkSignOffWatcher),
  fork(getTaskSettingsWatcher),
  fork(updateTaskSettingsWatcher),
  fork(getProjectDashboardWatcher),
  fork(getTemplateAssignmentsWatcher),
  fork(createTemplateAssignmentWatcher),
  fork(deleteTemplateAssignmentWatcher)
]
